/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import Raven from 'raven-js';
import { Icon, Message, Form, Input, Label } from 'semantic-ui-react';
import { Modal, Button } from '../../../../components';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { get } from 'lodash';

type EditManufacturerDialogProps = {
    intl: Object,
    trigger?: any,
    open: boolean,
    onClose: () => void,
    manufacturer: { id: string, name: string },
};

const intlMessages = defineMessages({
    manufacturerNamePlaceholder: {
        id: 'pages.settings.manufacturers.form.edit.manufacturerName',
        defaultMessage: 'Manufacturer name',
    },
    manufacturerNameError: {
        id: 'pages.settings.manufacturers.form.addForm.manufacturerNameError',
        defaultMessage: 'Enter manufacturer name',
    },
    existingsError: {
        id: 'pages.settings.manufacturers.form.existingsError',
        defaultMessage: 'This manufacturer does not exist',
    },
    nameExistingsError: {
        id: 'pages.settings.manufacturers.form.nameExistingsError',
        defaultMessage: 'Manufacturer with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const UPDATE_MANUFACTURER_MUTATION = gql`
    mutation updateManufacturer($input: UpdateManufacturerInput!) {
        updateManufacturer(input: $input) {
            manufacturer {
                id
                name
            }
        }
    }
`;

const EditManufacturerDialog = ({
    trigger,
    open,
    onClose,
    manufacturer,
    intl,
}: EditManufacturerDialogProps) => {
    const {
        control,
        handleSubmit,
        reset,
        setError,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({ defaultValues: { name: '' } });

    useEffect(() => {
        if (open && manufacturer) {
            reset({ name: manufacturer.name });
        }
    }, [open, manufacturer, reset]);

    const [updateManufacturer] = useMutation(UPDATE_MANUFACTURER_MUTATION);

    const onSubmit = async formData => {
        try {
            await updateManufacturer({
                variables: {
                    input: {
                        manufacturerId: manufacturer.id,
                        ...formData,
                    },
                },
            });
            onClose();
        } catch (error) {
            Raven.captureException(error);

            if (error.graphQLErrors && error.graphQLErrors.length) {
                const graphQLError = error.graphQLErrors[0];
                if (
                    graphQLError.name === 'NotExists' &&
                    get(graphQLError, 'data.error') === 'ID_DOES_NOT_EXISTS'
                ) {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.existingsError),
                    });
                    return;
                } else if (
                    graphQLError.name === 'AlreadyExists' &&
                    get(graphQLError, 'data.error') === 'NAME_ALREADY_EXISTS'
                ) {
                    setError('name', {
                        message: intl.formatMessage(intlMessages.nameExistingsError),
                    });
                    return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <Modal size="small" trigger={trigger} open={open} onClose={onClose}>
            <Modal.Header>
                <FormattedMessage
                    id="pages.settings.manufacturers.form.edit.edit"
                    defaultMessage="Edit Manufacturer"
                />
            </Modal.Header>
            <Modal.Content>
                {errors.root && (
                    <Message error>
                        <Icon name="warning sign" />
                        {errors.root.message}
                    </Message>
                )}
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Form.Field error={!!errors.name}>
                        <Controller
                            name="name"
                            control={control}
                            rules={{
                                required: intl.formatMessage(intlMessages.manufacturerNameError),
                            }}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input
                                    {...field}
                                    placeholder={intl.formatMessage(
                                        intlMessages.manufacturerNamePlaceholder,
                                    )}
                                    fluid
                                />
                            )}
                        />
                        {errors.name && (
                            <Label color="red" pointing basic>
                                {errors.name.message}
                            </Label>
                        )}
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    disabled={isSubmitting || !isDirty}
                    loading={isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                    primary
                >
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.settings.manufacturers.form.edit.save"
                        defaultMessage="Save"
                    />
                </Button>
                <Button disabled={isSubmitting} onClick={onClose} basic>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.settings.manufacturers.form.edit.cancel"
                        defaultMessage="Cancel"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default injectIntl(EditManufacturerDialog);
